/*
 * @Author: atdow
 * @Date: 2024-01-10 15:22:47
 * @LastEditors: null
 * @LastEditTime: 2024-12-06 16:50:44
 * @Description: file description
 */
import type { InternalAxiosRequestConfig } from 'axios'
import { RequestFrontPendingKey } from '/@/enums/index'
import { uuid } from '/@/utils/random'
import { cancelRequest } from '/@/api/common'

export const GlobalFiltersPendingByFuzzyKeyPrefix = 'global_filters'
export const PageFiltersPendingByFuzzyKeyPrefix = 'page_filters'
export const VisitMenuTrackedCancelKey = 'visit_menu_tracked'
const CancelRequestApiKey = 'cancel_request_api_key'

export interface AxiosRequestConfigWithSignal extends InternalAxiosRequestConfig {
  signal: AbortSignal
}

type PendingMap = Map<string, Canceler>

type Canceler = (message?: string) => void

const pendingMap: PendingMap = new Map()

type RequestIdInfo = {
  new: string // 新id
  old: string // 旧id
  cancelId: string // 需要cancel的id
}

type RequestIdMap = Map<string, RequestIdInfo>

const requestIdMap: RequestIdMap = new Map()

// 忽略cancel requestIds
const ignoreCancelRequestIds = [
  CancelRequestApiKey,
  GlobalFiltersPendingByFuzzyKeyPrefix,
  PageFiltersPendingByFuzzyKeyPrefix,
  '/user/permissions',
  '/customer/filter/group',
  '/customer/filter/template',
  VisitMenuTrackedCancelKey
]

const getPendingMap = () => {
  return pendingMap
}

const getPendingKey = (config: AxiosRequestConfigWithSignal): string => {
  let { data } = config
  const { url, method, params, headers } = config
  if (typeof data === 'string') data = JSON.parse(data)

  const requestFrontPendingKey = (params || {})[RequestFrontPendingKey] || (data || {})[RequestFrontPendingKey] || ''
  if (requestFrontPendingKey) {
    return requestFrontPendingKey
  }

  return [
    url,
    method,
    headers && headers.token ? headers.token : '',
    headers && headers.channel ? headers.channel : '',
    requestFrontPendingKey,
    JSON.stringify(params),
    JSON.stringify(data)
  ].join('&')
}

const addPending = (config: AxiosRequestConfigWithSignal): { requestIdInfo: RequestIdInfo } => {
  const controller = new AbortController()
  config.signal = controller.signal

  const pendingKey = getPendingKey(config)
  if (!pendingMap.has(pendingKey)) {
    pendingMap.set(pendingKey, controller.abort.bind(controller))
  }

  // 处理requestIdMap
  if (!requestIdMap.has(pendingKey)) {
    const newId = uuid()
    requestIdMap.set(pendingKey, {
      new: newId,
      old: '',
      cancelId: newId
    })
  } else {
    const info = requestIdMap.get(pendingKey)
    const newId = uuid()
    requestIdMap.set(pendingKey, {
      new: newId,
      old: info?.new || '',
      cancelId: newId
    })
  }

  return { requestIdInfo: requestIdMap.get(pendingKey) as RequestIdInfo }
}

const removePending = (config: AxiosRequestConfigWithSignal): void => {
  const { signal } = config
  if (signal?.aborted) {
    return
  }

  pendingMap.get(getPendingKey(config))?.('cancel')
  pendingMap.delete(getPendingKey(config))

  // 处理requestIdMap
  const requestIdInfo = requestIdMap.get(getPendingKey(config))
  if (requestIdInfo) {
    requestIdMap.set(getPendingKey(config), {
      ...requestIdInfo,
      cancelId: ''
    })
  }
}

const resolveCancelRequest = (cancelId: string) => {
  cancelRequest({ requestId: cancelId, [RequestFrontPendingKey]: CancelRequestApiKey })
}

/**
 * 根据key移除请求
 * @param key 匹配请求的key
 * @param isAccurateMatching 是否是精确匹配，默认为模糊匹配
 * @returns Promise<PendingMap>
 */
const removePendingByFuzzyKey = (key: string, isAccurateMatching?: boolean): Promise<PendingMap> => {
  for (const [ pendingKey, canceler ] of pendingMap) {
    if (isAccurateMatching) {
      if (pendingKey === key) {
        canceler('cancel')
        pendingMap.delete(pendingKey)
        break
      }
    } else if (pendingKey.includes(key)) {
      canceler('cancel')
      pendingMap.delete(pendingKey)
      break
    }
  }

  // 处理requestIdMap
  for (const [ pendingKey, info ] of requestIdMap) {
    const match = isAccurateMatching ? pendingKey === key : pendingKey.includes(key)
    if (match) {
      if (info?.cancelId && !ignoreCancelRequestIds.find(id => pendingKey.startsWith(id))) {
        resolveCancelRequest(info?.cancelId)
        requestIdMap.set(pendingKey, {
          ...info,
          cancelId: ''
        })
      }
      break
    }
  }

  return Promise.resolve(pendingMap)
}

const removePendingByFuzzyKeys = (keys: string[], isAccurateMatching?: boolean) => {
  keys.forEach(key => {
    removePendingByFuzzyKey(key, isAccurateMatching)
  })
}

/**
 * 清除requestIdMap
 */
const clearRequestIdMap = () => {
  requestIdMap.clear()
}

export const useAbortController = () => {
  return {
    getPendingMap,
    addPending,
    removePending,
    removePendingByFuzzyKey,
    removePendingByFuzzyKeys,
    clearRequestIdMap
  }
}
